import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Send, Sparkles, Phone, Info, AlertTriangle, ShieldCheck,
  Brain, Target, BookOpen, MessageSquare, Activity, PartyPopper, CheckCircle2, User,
  Flag, RotateCcw, Square, CheckSquare, Rocket, Check,
} from 'lucide-react';
import supabase from '../../lib/supabaseClient';
import { useAuth } from '../../context/AuthContext';

// ============================================================================
// TestConversationModal — a two-pane sandbox: an SMS thread inside a phone-device
// frame (left) + a live "Under the hood" educational panel (right) that explains
// the selected AI turn. The conversation OPENS with SurFox's real opening outreach
// (kickoff), then the tenant role-plays the lead replying. Stateless server-side;
// nothing is persisted and no SMS is ever sent.
//
// PRE-LAUNCH CONVERSATION GATE (additive, 2026-07-15): the tenant must drive
// REQUIRED test conversations to a natural terminal before the campaign's AI can
// be switched on. Terminals are judged per-exchange by Haiku in the edge function
// (never by turn count); when one fires, a checkbox appears and the tenant banks
// the attempt. Checked terminals persist in campaign_test_checkpoints, and the
// same status drives both the Go live lock and the server-side ai-toggle 409.
// ============================================================================

// Suggested lead openings are GENERATED per campaign by Haiku (industry + talk track
// + the persona in play), never hardcoded: a wholesale seller and a staffing candidate
// don't talk alike, and a generic list reads as fake. They are suggestions only — the
// composer is always free-text, which is how instant opt-outs stay reachable without
// being recommended (they end the thread on turn one and teach the tester nothing).
//
// Session cache, keyed by campaign id: the modal opens and resets often, and this must
// not re-spend on every open. Module scope so it survives unmount but not a reload.
// Generation is temperature 0, so a regenerated list comes back the same and the
// used-scenario markers (which match on label) keep matching.
const scenarioCache = new Map();

const TERMINAL_COPY = {
  disqualified: { label: 'Lead disqualified', tone: 'text-slate-700' },
  committed: { label: 'Lead committed', tone: 'text-green-700' },
  cold_no_path: { label: 'Went cold, no path forward', tone: 'text-slate-700' },
};

const STATUS_DOT = {
  'Hot Lead': 'bg-red-500',
  'Warm Lead': 'bg-amber-500',
  'Engaging': 'bg-blue-500',
  'Cold Lead': 'bg-slate-400',
};
const STAGES = ['Cold Lead', 'Warm Lead', 'Hot Lead'];
const stageIndex = (s) => (s === 'Hot Lead' ? 2 : s === 'Warm Lead' || s === 'Engaging' ? 1 : 0);

const ROUTE_BADGE = {
  REPLY: { label: 'Replied', cls: 'bg-green-100 text-green-700 border-green-200', Icon: MessageSquare },
  SILENT: { label: 'Stayed silent', cls: 'bg-slate-100 text-slate-600 border-slate-200', Icon: Phone },
  HARDCODED_DISCLOSURE: { label: 'Legal disclosure', cls: 'bg-violet-100 text-violet-700 border-violet-200', Icon: ShieldCheck },
  INITIAL: { label: 'Opening outreach', cls: 'bg-orange-100 text-orange-700 border-orange-200', Icon: Sparkles },
  // The lead asked for a call (or, on an opted-in account, said yes to one), so the same
  // gate production runs handed them to the team as Hot. See _shared/callbackRequest.ts.
  HANDOFF: { label: 'Handed to your team', cls: 'bg-red-100 text-red-700 border-red-200', Icon: Phone },
  // Review accounts only: the lead picked one of the real open times, the booking was recorded and the Hot Lead alert was sent.
  BOOKED: { label: 'Booked, Hot lead', cls: 'bg-red-100 text-red-700 border-red-200', Icon: Phone },
};

const ScoreBar = ({ label, value }) => (
  <div className="flex items-center gap-2.5">
    <span className="w-24 shrink-0 text-xs text-slate-500">{label}</span>
    <div className="h-2 flex-1 rounded-full bg-slate-100 overflow-hidden">
      <div className="h-full rounded-full bg-gradient-to-r from-orange-400 to-orange-500"
        style={{ width: `${Math.max(0, Math.min(100, Number(value) || 0))}%` }} />
    </div>
    <span className="w-8 shrink-0 text-right text-xs font-medium tabular-nums text-slate-700">
      {Number.isFinite(Number(value)) ? Math.round(Number(value)) : '–'}
    </span>
  </div>
);

const Chip = ({ tone = 'slate', children }) => {
  const tones = {
    green: 'border-green-200 bg-green-50 text-green-700',
    blue: 'border-blue-200 bg-blue-50 text-blue-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    slate: 'border-slate-200 bg-white text-slate-500',
  };
  return <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${tones[tone]}`}>{children}</span>;
};

// The right-hand educational panel for the currently-selected AI/initial turn.
const InsightPanel = ({ annotation, leadState }) => {
  if (!annotation) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-8 text-center text-sm text-slate-400">
        <Brain className="mb-3 h-8 w-8 text-slate-300" />
        Tap any SurFox message to see exactly what it detected and why it responded the way it did.
      </div>
    );
  }
  const badge = ROUTE_BADGE[annotation.route] || ROUTE_BADGE.REPLY;
  const p = annotation.psychology;
  // Once the lead is qualified + call-ready, production treats them as a Hot Lead
  // handoff — reflect that in the stepper so the finale lands.
  const idx = leadState.scheduling_mode ? 2 : stageIndex(leadState.status);

  return (
    <div className="flex h-full flex-col overflow-y-auto px-6 py-5">
      {/* Lead status progression */}
      <div className="mb-5">
        <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          <Activity className="h-3.5 w-3.5" /> Lead temperature
        </div>
        <div className="flex items-center gap-1.5">
          {STAGES.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex-1 rounded-md px-2 py-1.5 text-center text-[11px] font-medium transition ${
                i === idx ? 'bg-slate-900 text-white' : i < idx ? 'bg-slate-200 text-slate-600' : 'bg-slate-50 text-slate-400'
              }`}>{s.replace(' Lead', '')}</div>
              {i < STAGES.length - 1 && <span className="text-slate-300">→</span>}
            </React.Fragment>
          ))}
        </div>
        {leadState.scheduling_mode && (
          <div className="mt-2"><Chip tone="blue">Scheduling mode active</Chip></div>
        )}
      </div>

      {/* Route badge + intent */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${badge.cls}`}>
          <badge.Icon className="h-3.5 w-3.5" /> {badge.label}
        </span>
        {annotation.intent && (
          <span className="text-xs text-slate-500">
            intent: <span className="font-medium text-slate-700">{annotation.intent}</span>
            {Number.isFinite(annotation.intent_confidence)
              ? <span className="text-slate-400"> · {Math.round(annotation.intent_confidence * 100)}%</span> : null}
          </span>
        )}
      </div>

      {/* Hero read: the model's own per-turn analysis (dynamic), or the why for non-reply turns */}
      <p className="mb-3 text-[13px] leading-relaxed text-slate-700">
        {p?.summary || annotation.why}
      </p>

      {/* Decision verdict — what SurFox chose to do and why (reply turns only) */}
      {p && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-orange-100 bg-orange-50/70 p-3 text-[12px] leading-relaxed text-slate-700">
          <Target className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />
          <span><span className="font-semibold">Decision: </span>{annotation.why}</span>
        </div>
      )}

      {/* Psychological read */}
      {p && (
        <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3.5">
          <div className="mb-2.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            <Brain className="h-3.5 w-3.5" /> Psychological read
          </div>
          <div className="space-y-1.5">
            <ScoreBar label="Motivation" value={p.motivation} />
            <ScoreBar label="Urgency" value={p.urgency} />
            <ScoreBar label="Hesitation" value={p.hesitation} />
            <ScoreBar label="Sentiment" value={p.contextual_sentiment} />
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            <Chip tone={p.qualifying_complete ? 'green' : 'slate'}>
              {p.qualifying_complete ? 'Qualified ✓' : 'Not yet qualified'}
            </Chip>
            {p.call_readiness && <Chip tone="blue">Ready for a call</Chip>}
            {p.qualification_mismatch && <Chip tone="amber">Fit mismatch</Chip>}
          </div>
          {p.qualification_mismatch && p.mismatch_details && (
            <p className="mt-2 text-[11px] text-amber-700">{p.mismatch_details}</p>
          )}
        </div>
      )}

      {/* Finale — conversation goal reached (qualified + call-ready). */}
      {annotation.entered_scheduling && (
        <div className="mb-4 overflow-hidden rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50">
          <div className="flex items-center gap-2 border-b border-orange-100 px-3.5 py-2.5 text-[13px] font-bold text-orange-700">
            <PartyPopper className="h-4 w-4" /> Conversation goal reached
          </div>
          <div className="px-3.5 py-3 text-[12px] leading-relaxed text-slate-700">
            <p className="mb-2.5">SurFox qualified this lead and confirmed they’re ready to talk. In a real conversation, this is the handoff moment:</p>
            <ul className="space-y-1.5">
              {[
                'The lead flips to a Hot Lead',
                'Your team is notified instantly',
                'SurFox offers times from your live calendar and books the call',
                'Automated follow-ups keep it warm until the call',
              ].map((line, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-orange-500" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <p className="mt-3 rounded-lg bg-white/70 px-2.5 py-2 text-[11px] text-slate-500">
              Live booking runs on your real calendar, so it isn’t simulated in this test. This is where the conversation wraps.
            </p>
          </div>
        </div>
      )}

      {/* KB used — deduped by document. One source reads as a sentence; multiple
          sources list once each with a passage count. Per-chunk %s are noise. */}
      {annotation.kb_used?.length > 0 && (() => {
        const byTitle = new Map();
        annotation.kb_used.forEach((k) => {
          const t = k.title || 'Knowledge base';
          byTitle.set(t, (byTitle.get(t) || 0) + 1);
        });
        const sources = [...byTitle.entries()].map(([title, count]) => ({ title, count }));
        const total = annotation.kb_used.length;
        return (
          <div className="rounded-xl border border-slate-200 bg-white p-3.5">
            <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              <BookOpen className="h-3.5 w-3.5" /> Knowledge base used
            </div>
            {sources.length === 1 ? (
              <p className="text-[12px] leading-relaxed text-slate-600">
                Grounded its answer in <span className="font-medium text-slate-800">{sources[0].title}</span>
                {total > 1 ? ` (${total} passages).` : '.'}
              </p>
            ) : (
              <ul className="space-y-1">
                {sources.map((s, i) => (
                  <li key={i} className="flex items-center justify-between gap-2 text-[12px] text-slate-600">
                    <span className="truncate">{s.title}</span>
                    <span className="shrink-0 text-slate-400">{s.count > 1 ? `${s.count} passages` : '1 passage'}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })()}
    </div>
  );
};

// ── Review sandbox only: the knowledge-base document behind this campaign ─────────────────────────────
// Shown to reviewers (tenants.is_reviewer) next to the conversation, open by default, so they see WHAT the
// AI answers from. The text is the doc the business "uploaded" for this campaign (seeded on the reviewer
// template, see scripts/seed_reviewer_template_kb.mjs). Read-only. The chips are the doc's own FAQ questions:
// a reviewer would not otherwise think to ask something the document covers.
const KnowledgeDocPanel = ({ campaignId, onAsk }) => {
  const [docs, setDocs] = useState(null); // null = loading, [] = none
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: links, error: linkErr } = await supabase
          .from('campaign_knowledge_links').select('knowledge_id').eq('campaign_id', campaignId);
        if (linkErr) throw linkErr;
        const ids = (links || []).map((l) => l.knowledge_id);
        if (!ids.length) { if (!cancelled) setDocs([]); return; }
        const { data, error } = await supabase.from('knowledge_base').select('id, title, content').in('id', ids);
        if (error) throw error;
        if (!cancelled) setDocs(data || []);
      } catch (e) {
        console.warn('KnowledgeDocPanel: could not load the document', e?.message);
        if (!cancelled) { setFailed(true); setDocs([]); }
      }
    })();
    return () => { cancelled = true; };
  }, [campaignId]);

  const renderInline = (text) => text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => (
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i} className="font-semibold text-slate-800">{part.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{part}</React.Fragment>
  ));

  const renderBody = (content) => content.split('\n').map((line, i) => {
    const t = line.trim();
    if (!t) return null;
    if (t.startsWith('## ')) return <h4 key={i} className="mt-4 mb-1 text-[12px] font-semibold text-slate-800">{t.slice(3)}</h4>;
    return <p key={i} className="mb-1.5 text-[12px] leading-relaxed text-slate-600">{renderInline(t)}</p>;
  });

  // "**Question?** answer" lines from the doc's FAQ become suggested things to ask.
  const questionsOf = (content) => (content || '').split('\n')
    .map((l) => (l.trim().match(/^\*\*(.+?\?)\*\*/) || [])[1]).filter(Boolean).slice(0, 3);

  const doc = docs && docs[0];
  return (
    <div className="order-3 flex max-h-[70vh] min-h-0 w-full shrink-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white lg:order-4 lg:w-[340px]">
      <div className="flex items-center gap-1.5 border-b border-slate-100 px-5 py-3 text-[12px] font-semibold text-slate-700">
        <BookOpen className="h-4 w-4 text-orange-500" /> Knowledge base for this campaign
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        <div className="mb-3 rounded-xl border border-orange-100 bg-orange-50 px-3 py-2.5 text-[11.5px] leading-relaxed text-slate-700">
          This is the document the business uploaded for this campaign. Before every reply, the AI looks
          through it for the passages that answer what the lead just asked, so it answers from the
          business&apos;s own facts instead of guessing. Try asking something it covers.
        </div>
        {docs === null && <p className="text-[12px] text-slate-400">Loading…</p>}
        {docs && !doc && (
          <p className="text-[12px] text-slate-400">{failed ? 'The document could not be loaded right now.' : 'No document is attached to this campaign.'}</p>
        )}
        {doc && (
          <>
            <div className="mb-2 text-[13px] font-semibold leading-snug text-slate-800">{doc.title}</div>
            {questionsOf(doc.content).length > 0 && (
              <div className="mb-3 flex flex-col gap-1.5">
                {questionsOf(doc.content).map((q) => (
                  <button key={q} type="button" onClick={() => onAsk(q)}
                    className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-left text-[11.5px] text-slate-600 transition hover:border-orange-300 hover:bg-orange-50">
                    Ask: {q}
                  </button>
                ))}
              </div>
            )}
            <div>{renderBody(doc.content || '')}</div>
          </>
        )}
      </div>
    </div>
  );
};

export default function TestConversationModal({ campaign, onClose, apiBase, fetchWithCsrf, onGateCleared }) {
  const { user: authUser } = useAuth() || {};
  const isReviewer = authUser?.is_reviewer === true;
  const [transcript, setTranscript] = useState([]); // {role, text, sent?, annotation?}
  const [leadState, setLeadState] = useState({ status: 'Cold Lead', scheduling_mode: false, qualifying_complete: false, turn_count: 0 });
  const [persona, setPersona] = useState(null); // { name, custom_fields, scenario } — Haiku-generated on kickoff, held fixed for the run
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [kicking, setKicking] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null); // index of selected AI/initial turn

  // ── Pre-launch gate state (additive) ──
  const [terminal, setTerminal] = useState(null);   // { reached, reason, rationale } for the CURRENT attempt
  const [banked, setBanked] = useState(false);      // has this attempt been checked off?
  const [rejected, setRejected] = useState(null);   // { message, retryable } — scenario repeats one already banked
  const [gate, setGate] = useState(null);           // { required, count, cleared, skipped, rationale }
  const [gateBusy, setGateBusy] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [runId, setRunId] = useState(0);            // bumped to reset the conversation (re-runs kickoff)
  const [scenarios, setScenarios] = useState(() => scenarioCache.get(campaign?.id) || null);
  const [scenariosLoading, setScenariosLoading] = useState(!scenarioCache.has(campaign?.id));

  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const kicked = useRef(0); // guards StrictMode double-invoke; holds the runId already kicked
  // Suggested scenario this attempt started from (null when free-typed). Recorded on
  // the checkpoint so the panel can mark it used. A ref, not state: it must not
  // re-render the thread, and only its value at bank time matters.
  const scenarioRef = useRef(null);

  const aiName = campaign?.ai_representative_name || 'SurFox';

  // Load gate status on mount. This is also what triggers the smart-skip comparison
  // server-side (evaluated once, then cached against the talk track's fingerprint).
  const loadGate = useCallback(async () => {
    if (!apiBase || !fetchWithCsrf) return;
    try {
      const res = await fetchWithCsrf(`${apiBase}/campaign-test-gate/${campaign.id}`);
      if (res.ok) setGate(await res.json());
    } catch { /* gate status is advisory here; the server enforces on launch */ }
  }, [apiBase, fetchWithCsrf, campaign?.id]);

  useEffect(() => { loadGate(); }, [loadGate]);

  // Tell the demo guide (Surf) this window is open, and when it closes, so it can explain what the prospect is looking at.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('surf:demo-context', { detail: { modal: 'test_conversation' } }));
    return () => window.dispatchEvent(new CustomEvent('surf:demo-context', { detail: { modal: null } }));
  }, []);

  // Smart skip: this campaign's talk track is not meaningfully different from one
  // the tenant already proved out, so there is nothing to learn by re-testing it.
  // Go straight to the confirmation rather than making them role-play three
  // conversations against messaging they have already approved. Fires once.
  const skipShown = useRef(false);
  useEffect(() => {
    if (gate?.skipped && !skipShown.current) {
      skipShown.current = true;
      setShowConfirm(true);
    }
  }, [gate?.skipped]);

  // Auto-load the opening outreach on mount, and again on every reset (runId bump).
  useEffect(() => {
    if (kicked.current === runId + 1) return;
    kicked.current = runId + 1;
    (async () => {
      setKicking(true);
      try {
        const { data, error: invokeError } = await supabase.functions.invoke('simulate-conversation', {
          body: { campaign_id: campaign.id, kickoff: true },
        });
        if (invokeError || data?.error) {
          let msg = data?.error || 'Could not load the opening message.';
          try { const ctx = await invokeError?.context?.json?.(); if (ctx?.error) msg = ctx.error; } catch { /* ignore */ }
          setError(msg);
        } else {
          setTranscript([{ role: 'ai', text: data.reply?.text || '', sent: true, annotation: data.annotation }]);
          setSelected(0);
          if (data.leadState) setLeadState(data.leadState);
          if (data.persona) setPersona(data.persona);
        }
      } catch {
        setError('Could not load the opening message.');
      } finally {
        setKicking(false);
        inputRef.current?.focus();
      }
    })();
  }, [campaign?.id, runId]);

  // Suggested scenarios, generated for THIS campaign. Waits for the persona so the
  // suggestions fit the lead being played, then caches per campaign — a reset makes a
  // new persona but must not re-spend on a new panel.
  useEffect(() => {
    const cid = campaign?.id;
    if (!cid) return undefined;
    if (scenarioCache.has(cid)) {
      setScenarios(scenarioCache.get(cid));
      setScenariosLoading(false);
      return undefined;
    }
    if (!persona) return undefined; // kickoff hasn't produced the lead yet
    let cancelled = false;
    (async () => {
      setScenariosLoading(true);
      try {
        const { data } = await supabase.functions.invoke('simulate-conversation', {
          body: { campaign_id: cid, scenarios: true, persona },
        });
        const list = Array.isArray(data?.scenarios) ? data.scenarios : [];
        if (cancelled) return;
        // Only cache a real list: caching a failure would strand the panel empty for
        // the whole session with no way to retry.
        if (list.length) scenarioCache.set(cid, list);
        setScenarios(list);
      } catch {
        if (!cancelled) setScenarios([]);
      } finally {
        if (!cancelled) setScenariosLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [campaign?.id, persona]);

  // Start a fresh attempt: new persona, empty thread, no terminal. Nothing already
  // banked is touched — a reset costs the tenant only the attempt in progress.
  const resetConversation = useCallback(() => {
    setTranscript([]);
    setSelected(null);
    setLeadState({ status: 'Cold Lead', scheduling_mode: false, qualifying_complete: false, turn_count: 0 });
    setPersona(null);
    setTerminal(null);
    setBanked(false);
    setRejected(null);
    setDraft('');
    setError(null);
    scenarioRef.current = null; // a new attempt starts from no scenario
    setRunId((n) => n + 1);
  }, []);

  // Recalibrate in place: SurfWidget lives at z-[10001] and this modal at z-[60], so
  // the Surf panel slides in OVER the still-mounted test conversation — the tenant
  // never leaves the screen. Same event contract LaunchConsole's button uses.
  const openRecalibrate = useCallback(() => {
    window.dispatchEvent(new CustomEvent('surf:recalibrate', {
      detail: { campaignId: campaign.id, campaignName: campaign.name },
    }));
    // The talk track may now differ, so the attempt in progress is testing stale
    // copy. Reset it; the retry does not count until it reaches its own terminal.
    resetConversation();
    // A new track fingerprint invalidates any cached skip decision server-side.
    loadGate();
  }, [campaign?.id, campaign?.name, resetConversation, loadGate]);

  // Bank the current attempt. The server re-validates and enforces the ceiling.
  const bankTerminal = useCallback(async () => {
    if (!terminal?.reached || banked || gateBusy || !apiBase || !fetchWithCsrf) return;
    setGateBusy(true);
    try {
      const res = await fetchWithCsrf(`${apiBase}/campaign-test-gate/${campaign.id}/checkpoint`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transcript: transcript.map((t) => ({ role: t.role, text: t.text, sent: t.sent })),
          terminal_reason: terminal.reason,
          scenario_label: scenarioRef.current,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        // Repeat of a scenario already banked, or the check couldn't run. Either way
        // the attempt is NOT counted — show why, in place, and let them try another
        // angle. Not routed through `error` (the red API-failure strip); this is
        // normal guidance, not a fault.
        if (body.error === 'DUPLICATE_SCENARIO' || body.error === 'DIVERSITY_CHECK_UNAVAILABLE') {
          // The server's message now says WHY and which ending to try next (2026-09-10).
          setRejected({ message: body.message, retryable: body.error === 'DIVERSITY_CHECK_UNAVAILABLE' });
          // Refresh the gate so the "next one counts automatically" hint appears as soon
          // as the tester has earned it.
          if (body.error === 'DUPLICATE_SCENARIO') loadGate();
          return;
        }
        setError(body.error || 'Could not save this conversation.');
        return;
      }
      const next = await res.json();
      setGate(next);
      setBanked(true);
      setRejected(null);
      if (next.cleared) setShowConfirm(true);
    } catch {
      setError('Could not save this conversation.');
    } finally {
      setGateBusy(false);
    }
  }, [terminal, banked, gateBusy, apiBase, fetchWithCsrf, campaign?.id, transcript, loadGate]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [transcript, sending, kicking]);

  const send = useCallback(async () => {
    const text = draft.trim();
    // Once an attempt has terminated the thread is closed — the tenant banks it or
    // resets. Letting them keep texting past a terminal would make the verdict
    // meaningless (and the banked transcript would not match what they approved).
    if (!text || sending || kicking || terminal?.reached) return;
    setError(null);
    setSending(true);
    const priorTranscript = transcript.map((t) => ({ role: t.role, text: t.text }));
    setTranscript((prev) => [...prev, { role: 'lead', text }]);
    setDraft('');

    try {
      const { data, error: invokeError } = await supabase.functions.invoke('simulate-conversation', {
        body: { campaign_id: campaign.id, newMessage: text, transcript: priorTranscript, leadState, persona },
      });
      if (invokeError || data?.error) {
        let msg = data?.error || 'Something went wrong reaching SurFox.';
        try { const ctx = await invokeError?.context?.json?.(); if (ctx?.error) msg = ctx.error; } catch { /* ignore */ }
        setError(msg);
        return;
      }
      setTranscript((prev) => {
        const next = [...prev, { role: 'ai', text: data.reply?.text || null, sent: !!data.reply?.sent, annotation: data.annotation }];
        setSelected(next.length - 1);
        return next;
      });
      if (data.leadState) setLeadState(data.leadState);
      // Terminal verdict for this exchange, judged by Haiku over the whole thread.
      // Reason-less or unreached verdicts leave the conversation open.
      if (data.terminal?.reached) setTerminal(data.terminal);
    } catch {
      setError('Something went wrong reaching SurFox.');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }, [draft, sending, kicking, terminal, transcript, leadState, persona, campaign?.id]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  // Suggestions load the composer rather than firing — the tenant can edit first,
  // which keeps "or type your own" true rather than decorative. The chip is only a
  // starting point, so edits are expected and keep the label.
  // (Not named use* — the react-hooks rule reads that prefix as a Hook.)
  const applyScenario = useCallback((label, text) => {
    setDraft(text);
    scenarioRef.current = label;
    inputRef.current?.focus();
  }, []);

  const selectedAnnotation = selected != null ? transcript[selected]?.annotation : null;
  const done = gate?.count ?? 0;
  const required = gate?.required ?? 3;
  const threadClosed = !!terminal?.reached;
  const usedScenarios = gate?.used_scenarios || [];

  return createPortal(
    <div className={`fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto bg-black/55 p-4 font-sans antialiased${authUser?.is_prospect_demo ? ' lg:pr-[396px] pb-[46vh] lg:pb-4' : ''}`}
      onClick={onClose}>
      <div className={`relative flex max-h-[90vh] w-full ${isReviewer ? 'max-w-[1500px]' : 'max-w-[1140px]'} flex-col overflow-hidden rounded-2xl bg-white shadow-[0_30px_80px_rgba(18,30,50,.35)]`}
        onClick={(e) => e.stopPropagation()}>

        {/* Top bar */}
        <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-5 py-3.5">
          <div className="flex min-w-0 items-center gap-2.5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-orange-500 text-white">
              <MessageSquare className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold leading-tight text-slate-800">Test conversation with SurFox</div>
              <div className="truncate text-[11px] leading-tight text-slate-500">{campaign?.name} · {isReviewer ? 'no texts are sent; a booked time is saved as a sample lead' : 'nothing here is sent or saved'}</div>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {/* Checkpoint tracker — the gate's progress, always visible. */}
            {gate && !gate.skipped && (
              <div className="hidden items-center gap-2 rounded-full border border-slate-200 bg-slate-50 py-1 pl-2.5 pr-3 sm:flex"
                title="Each conversation you take to a natural ending counts once.">
                <span className="flex items-center gap-1">
                  {Array.from({ length: required }).map((_, i) => (
                    <span key={i} className={`h-1.5 w-4 rounded-full transition-colors ${i < done ? 'bg-green-500' : 'bg-slate-200'}`} />
                  ))}
                </span>
                <span className="text-[11px] font-medium tabular-nums text-slate-600">
                  {done} of {required} conversations completed
                </span>
              </div>
            )}
            {gate?.skipped && (
              <span className="hidden items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-2.5 py-1 text-[11px] font-medium text-green-700 sm:inline-flex">
                <CheckCircle2 className="h-3.5 w-3.5" /> Testing not required
              </span>
            )}

            {/* Recalibrate without leaving the sandbox. */}
            {campaign?.custom_talk_track_id && (
              <button onClick={openRecalibrate}
                title="Adjust this campaign's messaging with Surf, then retry"
                className="inline-flex items-center gap-1.5 rounded-lg border border-orange-200 bg-white px-2.5 py-1.5 text-[12px] font-semibold text-orange-600 transition hover:bg-orange-50">
                <Sparkles className="h-3.5 w-3.5" /> Recalibrate
              </button>
            )}
            <button onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Close">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Body: scenarios + phone + insight panel */}
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto bg-slate-50 p-5 lg:flex-row lg:overflow-visible">
          {/* Suggested scenarios — starting points only; the composer stays free-text. */}
          <div className="order-2 w-full shrink-0 lg:order-1 lg:w-[196px]">
            <div className="rounded-2xl border border-slate-200 bg-white p-3.5">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                <Flag className="h-3.5 w-3.5" /> Suggested scenarios
              </div>
              <p className="mt-1 text-[10.5px] italic leading-snug text-slate-400">or type your own</p>
              {/* Say up front what "3 conversations" means, so nobody learns it from a
                  refusal (Exodus, 2026-09-10: two refusals, then a manual waiver). */}
              {gate && !gate.cleared && (
                <p className="mt-2 rounded-lg bg-slate-50 px-2 py-1.5 text-[10.5px] leading-snug text-slate-500">
                  Launch needs {required} different conversations. Mix how they end: the lead agrees or says no.
                </p>
              )}
              {/* Review accounts only: point at the part that works for real here (slots, booking, the Hot Lead email). */}
              {isReviewer && (
                <p className="mt-2 rounded-lg border border-orange-200 bg-orange-50 px-2 py-1.5 text-[10.5px] font-medium leading-snug text-orange-700">
                  Try asking for a call (the AI may ask a qualifying question first, so answer it), then pick a time and check your inbox. The booking confirmation in this window is a fixed template; live, the AI writes it in the lead's own words and names who will call, taken from your talk track.
                </p>
              )}
              {gate?.next_counts_automatically && !gate.cleared && (
                <p className="mt-1.5 rounded-lg border border-green-200 bg-green-50 px-2 py-1.5 text-[10.5px] font-medium leading-snug text-green-700">
                  Your next finished conversation counts automatically.
                </p>
              )}
              {/* Generating — the panel is written for this campaign, so it can't
                  render until Haiku answers. */}
              {scenariosLoading && (
                <div className="mt-2.5 space-y-1.5">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="h-[30px] animate-pulse rounded-lg bg-slate-100" />
                  ))}
                  <p className="pt-1 text-[10.5px] leading-snug text-slate-400">
                    Reading your campaign to suggest what real leads might say…
                  </p>
                </div>
              )}

              {!scenariosLoading && scenarios?.length === 0 && (
                <p className="mt-2.5 text-[11px] leading-snug text-slate-400">
                  Couldn&rsquo;t put together suggestions for this campaign. Type as the lead in your own words.
                </p>
              )}

              {!scenariosLoading && scenarios?.length > 0 && (
                <div className="mt-2.5 flex flex-wrap gap-1.5 lg:flex-col lg:flex-nowrap">
                  {scenarios.map((s) => {
                    // Already counted toward launch. Marked, not blocked: they may still
                    // want to run it, it just won't count again. The marker is the nudge
                    // to try something else.
                    const used = usedScenarios.includes(s.label);
                    return (
                      <button key={s.label} onClick={() => applyScenario(s.label, s.text)}
                        disabled={kicking || sending || threadClosed}
                        title={used ? "Already counted toward launch. You can run it again, but it won't count." : s.text}
                        className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-left text-[12px] font-medium transition disabled:cursor-not-allowed disabled:opacity-50 lg:w-full ${
                          used
                            ? 'border-slate-200 bg-slate-100 text-slate-400 hover:bg-slate-200/70'
                            : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700'
                        }`}>
                        {used && <Check className="h-3 w-3 shrink-0 text-green-600" />}
                        <span className={used ? 'line-through decoration-slate-400/70' : ''}>{s.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              {usedScenarios.length > 0 && !gate?.cleared && (
                <p className="mt-2 border-t border-slate-100 pt-2 text-[10.5px] leading-snug text-slate-400">
                  Checked scenarios are already counted. Try a different angle for the next one.
                </p>
              )}
            </div>
            {/* Start over — never touches conversations already banked. */}
            <button onClick={resetConversation} disabled={kicking}
              className="mt-2.5 inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white py-2 text-[12px] font-semibold text-slate-500 transition hover:bg-slate-50 hover:text-slate-700 disabled:opacity-50">
              <RotateCcw className="h-3.5 w-3.5" /> Start a new conversation
            </button>
          </div>

          {/* Phone device */}
          <div className="order-1 mx-auto w-[330px] shrink-0 lg:order-2">
            <div className="rounded-[2.4rem] border-[10px] border-slate-900 bg-slate-900 shadow-xl">
              <div className="flex h-[560px] flex-col overflow-hidden rounded-[1.7rem] bg-white">
                {/* notch / contact header */}
                <div className="relative flex flex-col items-center border-b border-slate-100 bg-slate-50 pb-2.5 pt-2">
                  <div className="mb-2 h-1 w-16 rounded-full bg-slate-300" />
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-500 text-white">
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <div className="mt-1 text-[12px] font-semibold text-slate-800">{aiName}</div>
                  <div className="text-[10px] text-slate-400">SurFox AI</div>
                </div>

                {/* thread */}
                <div ref={scrollRef} className="flex-1 space-y-2.5 overflow-y-auto bg-white px-3 py-3">
                  {kicking && (
                    <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
                      <span className="inline-flex gap-1">
                        <span className="h-2 w-2 animate-bounce rounded-full bg-orange-400 [animation-delay:-0.3s]" />
                        <span className="h-2 w-2 animate-bounce rounded-full bg-orange-400 [animation-delay:-0.15s]" />
                        <span className="h-2 w-2 animate-bounce rounded-full bg-orange-400" />
                      </span>
                      <p className="max-w-[220px] text-[11.5px] leading-snug text-slate-500">
                        Studying this campaign and building a realistic lead to test against…
                      </p>
                    </div>
                  )}

                  {transcript.map((t, i) => {
                    if (t.role === 'lead') {
                      return (
                        <div key={i} className="flex justify-end">
                          <div className="max-w-[80%] rounded-2xl rounded-br-md bg-orange-500 px-3 py-2 text-[13px] text-white shadow-sm">{t.text}</div>
                        </div>
                      );
                    }
                    const isSel = i === selected;
                    return (
                      <div key={i} className="flex flex-col items-start">
                        {t.sent ? (
                          <button onClick={() => setSelected(i)}
                            className={`max-w-[82%] rounded-2xl rounded-bl-md px-3 py-2 text-left text-[13px] text-slate-800 shadow-sm transition ${
                              isSel ? 'bg-white ring-2 ring-orange-400' : 'bg-slate-100 hover:bg-slate-200/70'}`}>
                            {t.text}
                          </button>
                        ) : (
                          <button onClick={() => setSelected(i)}
                            className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] italic text-slate-500 transition ${
                              isSel ? 'bg-slate-200 ring-2 ring-orange-300' : 'bg-slate-100 hover:bg-slate-200/70'}`}>
                            <Phone className="h-3 w-3" /> SurFox stayed silent
                          </button>
                        )}
                        {t.annotation?.entered_scheduling && (
                          <div className="mt-2 w-full rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 px-3 py-2.5 text-center text-white shadow">
                            <div className="flex items-center justify-center gap-1.5 text-[12px] font-bold">
                              <PartyPopper className="h-3.5 w-3.5" /> Goal reached
                            </div>
                            <div className="mt-0.5 text-[10.5px] opacity-90">Qualified &amp; ready for a call — SurFox would book it now</div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {sending && (
                    <div className="flex justify-start">
                      <div className="rounded-2xl rounded-bl-md bg-slate-100 px-3 py-2.5">
                        <span className="inline-flex gap-1">
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.3s]" />
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.15s]" />
                          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" />
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Terminal reached — the gate's checkbox. Appears only when Haiku
                      judges the conversation genuinely concluded, never on turn count. */}
                  {threadClosed && (
                    <div className="mt-1 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                        <Flag className="h-3.5 w-3.5" /> Conversation ended
                      </div>
                      <div className={`mt-1 text-[12.5px] font-semibold ${TERMINAL_COPY[terminal.reason]?.tone || 'text-slate-700'}`}>
                        {TERMINAL_COPY[terminal.reason]?.label || 'Reached an ending'}
                      </div>
                      {terminal.rationale && (
                        <p className="mt-1 text-[11px] leading-snug text-slate-500">{terminal.rationale}</p>
                      )}
                      {/* Scenario repeats one already banked — it does not count. */}
                      {rejected && !banked && (
                        <div className="mt-2.5 rounded-xl border border-amber-200 bg-amber-50 p-2.5">
                          <div className="flex items-start gap-1.5 text-[11.5px] font-medium leading-snug text-amber-800">
                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span>{rejected.message}</span>
                          </div>
                        </div>
                      )}
                      <button onClick={bankTerminal} disabled={banked || gateBusy || (rejected && !rejected.retryable)}
                        className={`mt-2.5 flex w-full items-center gap-2 rounded-xl border px-3 py-2 text-left text-[12px] font-semibold transition ${
                          banked
                            ? 'cursor-default border-green-200 bg-green-50 text-green-700'
                            : 'border-slate-300 bg-white text-slate-700 hover:border-orange-300 hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-60'
                        }`}>
                        {banked
                          ? <CheckSquare className="h-4 w-4 shrink-0 text-green-600" />
                          : <Square className="h-4 w-4 shrink-0 text-slate-400" />}
                        <span>
                          {banked ? 'Counted toward launch'
                            : gateBusy ? 'Saving…'
                            : rejected && !rejected.retryable ? "Doesn't count"
                            : rejected?.retryable ? 'Try again'
                            : 'Count this conversation'}
                        </span>
                      </button>
                      {/* A rejected scenario has one way forward: go test a different one. */}
                      {rejected && !rejected.retryable && (
                        <button onClick={resetConversation}
                          className="mt-1.5 inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-slate-900 py-2 text-[12px] font-semibold text-white transition hover:bg-slate-700">
                          <RotateCcw className="h-3.5 w-3.5" /> Try a different scenario
                        </button>
                      )}
                      {banked && !gate?.cleared && (
                        <button onClick={resetConversation}
                          className="mt-1.5 inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-slate-900 py-2 text-[12px] font-semibold text-white transition hover:bg-slate-700">
                          <RotateCcw className="h-3.5 w-3.5" /> Next conversation
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* composer */}
                <div className="flex items-end gap-2 border-t border-slate-100 bg-white px-2.5 py-2.5">
                  <textarea ref={inputRef} value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={onKeyDown}
                    rows={1}
                    placeholder={kicking ? 'Preparing your test lead…' : threadClosed ? 'This conversation has ended' : 'Text as the lead…'}
                    disabled={kicking || threadClosed}
                    className="max-h-20 flex-1 resize-none rounded-2xl border border-slate-200 bg-slate-50 px-3 py-1.5 text-[13px] text-slate-800 outline-none placeholder:text-slate-400 focus:border-orange-300 focus:bg-white disabled:opacity-60" />
                  <button onClick={send} disabled={!draft.trim() || sending || kicking || threadClosed}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-500 text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
                    aria-label="Send">
                    <Send className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
            {persona && (persona.name || Object.keys(persona.custom_fields || {}).length > 0) ? (
              <div className="mt-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left shadow-sm">
                <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                  <User className="h-3 w-3" /> You’re texting as
                </div>
                <div className="mt-0.5 text-[13px] font-semibold text-slate-800">{persona.name || 'Test lead'}</div>
                {persona.scenario && (
                  <p className="mt-0.5 text-[11px] leading-snug text-slate-500">{persona.scenario}</p>
                )}
                {Object.keys(persona.custom_fields || {}).length > 0 && (
                  <div className="mt-2 space-y-0.5 border-t border-slate-100 pt-2">
                    {Object.entries(persona.custom_fields).map(([k, v]) => (
                      <div key={k} className="flex items-start justify-between gap-2 text-[11px]">
                        <span className="shrink-0 text-slate-400">{k}</span>
                        <span className="text-right font-medium text-slate-700">{String(v)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <p className="mt-2 text-[9.5px] italic text-slate-400">{isReviewer
                  ? 'Fictitious lead generated for this test. If you book a time, it is saved as a sample lead (it counts in Calls Booked) and you get the real alert email.'
                  : 'Fictitious lead generated for this test — nothing here is real or saved.'}</p>
              </div>
            ) : (
              <p className="mt-2 text-center text-[10.5px] text-slate-400">You’re texting as the lead</p>
            )}
          </div>

          {/* Insight panel */}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <div className="flex items-center gap-1.5 border-b border-slate-100 px-6 py-3 text-[12px] font-semibold text-slate-700">
              <Info className="h-4 w-4 text-orange-500" /> Under the hood
            </div>
            <div className="min-h-0 flex-1">
              <InsightPanel annotation={selectedAnnotation} leadState={leadState} />
            </div>
            {error && (
              <div className="flex items-center gap-1.5 border-t border-red-100 bg-red-50 px-6 py-2.5 text-[12px] text-red-600">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {error}
              </div>
            )}
          </div>

          {/* Review sandbox only: the document the AI answers from. Open by default. */}
          {isReviewer && (
            <KnowledgeDocPanel
              campaignId={campaign.id}
              onAsk={(q) => { setDraft(q); inputRef.current?.focus(); }}
            />
          )}
        </div>
        {/* Gate cleared — confirm and go live. Nested inside the modal so the tenant
            confirms in the same place they did the testing. */}
        {showConfirm && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-900/45 p-6"
            onClick={() => setShowConfirm(false)}>
            <div className="w-full max-w-[380px] rounded-2xl bg-white p-6 text-center shadow-2xl"
              onClick={(e) => e.stopPropagation()}>
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-green-100 text-green-600">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <h3 className="mt-3 text-[16px] font-bold text-slate-800">
                {gate?.skipped ? 'This campaign is ready' : `You've tested ${required} scenarios`}
              </h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-slate-500">
                {gate?.skipped
                  ? gate.rationale || 'This campaign matches messaging you already approved, so it does not need its own testing.'
                  : `You've tested ${required} scenarios and approved this campaign. Ready to go live?`}
              </p>
              <div className="mt-5 flex gap-2">
                <button onClick={() => setShowConfirm(false)}
                  className="flex-1 rounded-xl border border-slate-200 bg-white py-2.5 text-[13px] font-semibold text-slate-600 transition hover:bg-slate-50">
                  Not yet
                </button>
                <button onClick={() => { setShowConfirm(false); onGateCleared?.(campaign); }}
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-gradient-to-br from-cyan-400 to-cyan-600 py-2.5 text-[13px] font-bold text-white shadow-lg shadow-cyan-500/30 transition hover:brightness-105">
                  <Rocket className="h-4 w-4" /> Go live
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
